/**
 * DEVLOGS-TEMP — per-namespace token buckets.
 *
 * "Everything on" is a lie if a limiter silently eats records, so every
 * rejection is counted and the count rides out on the next admitted record in
 * that namespace (the suppression-piggyback trick `MetricThrottle` uses).
 *
 * Rule selection is longest-prefix, and the resolved rule is memoised into a map
 * that CLEARS past a cap — an unbounded map keyed by caller-supplied strings is
 * how a logger becomes the leak it was installed to find.
 */

export interface RateLimitRule {
  /** Namespace prefix. "" is the global backstop. */
  ns: string;
  perSec: number;
  burst: number;
}

/**
 * Defaults tuned to the real event rates in this codebase, not to round numbers.
 * `media.timeupdate` is coalesced upstream; this is only the backstop.
 */
export const DEFAULT_RATE_LIMITS: readonly RateLimitRule[] = Object.freeze([
  { ns: "", perSec: 50, burst: 100 },
  { ns: "media.timeupdate", perSec: 0.2, burst: 2 },
  { ns: "media", perSec: 10, burst: 30 },
  { ns: "net.fetch", perSec: 20, burst: 40 },
  { ns: "net", perSec: 20, burst: 40 },
  { ns: "ws", perSec: 20, burst: 60 },
  { ns: "console", perSec: 10, burst: 30 },
  { ns: "core.drift", perSec: 1, burst: 5 },
]);

export interface AdmitVerdict {
  allow: boolean;
  /** Drops accumulated in this namespace since the last admit. 0 unless allowed. */
  dropped: number;
}

interface Bucket {
  tokens: number;
  last: number;
  rule: RateLimitRule;
}

const RULE_MEMO_CAP = 256;

export class NamespaceRateLimiter {
  readonly #rules: readonly RateLimitRule[];
  readonly #global: RateLimitRule;
  readonly #now: () => number;
  #buckets = new Map<string, Bucket>();
  #ruleMemo = new Map<string, RateLimitRule>();
  #drops = new Map<string, number>();
  #globalBucket: Bucket;

  constructor(
    rules: readonly RateLimitRule[] = DEFAULT_RATE_LIMITS,
    now: () => number = Date.now,
  ) {
    this.#rules = rules;
    this.#now = now;
    const global = rules.find((rule) => rule.ns === "") ?? {
      ns: "",
      perSec: 50,
      burst: 100,
    };
    this.#global = global;
    // The injected clock is guarded even here: constructing a limiter must not
    // be able to throw, or a hostile clock takes down createDevLogger itself —
    // outside every try/catch. The contract test pins this.
    this.#globalBucket = { tokens: global.burst, last: this.#safeNow(), rule: global };
  }

  /** Never throws, never returns NaN. */
  #safeNow(): number {
    try {
      const value = this.#now();
      return Number.isFinite(value) ? value : 0;
    } catch {
      return 0;
    }
  }

  admit(ns: string, now = this.#safeNow()): AdmitVerdict {
    // The global backstop applies IN ADDITION to the specific rule.
    if (!this.#take(this.#globalBucket, now)) {
      this.#note(ns);
      return { allow: false, dropped: 0 };
    }

    const bucket = this.#bucketFor(ns, now);
    if (!this.#take(bucket, now)) {
      this.#note(ns);
      return { allow: false, dropped: 0 };
    }

    const dropped = this.#drops.get(ns) ?? 0;
    if (dropped > 0) this.#drops.delete(ns);
    return { allow: true, dropped };
  }

  /** Drops not yet piggybacked, for the flush envelope. Non-destructive. */
  pendingDrops(): Record<string, number> {
    return Object.fromEntries(this.#drops);
  }

  takeDrops(): Record<string, number> {
    const out = Object.fromEntries(this.#drops);
    this.#drops.clear();
    return out;
  }

  reset(): void {
    this.#buckets.clear();
    this.#ruleMemo.clear();
    this.#drops.clear();
    this.#globalBucket = {
      tokens: this.#global.burst,
      last: this.#safeNow(),
      rule: this.#global,
    };
  }

  #note(ns: string): void {
    if (this.#drops.size >= RULE_MEMO_CAP && !this.#drops.has(ns)) return;
    this.#drops.set(ns, (this.#drops.get(ns) ?? 0) + 1);
  }

  #bucketFor(ns: string, now: number): Bucket {
    const existing = this.#buckets.get(ns);
    if (existing) return existing;
    const rule = this.#ruleFor(ns);
    const bucket: Bucket = { tokens: rule.burst, last: now, rule };
    if (this.#buckets.size >= RULE_MEMO_CAP) this.#buckets.clear();
    this.#buckets.set(ns, bucket);
    return bucket;
  }

  #ruleFor(ns: string): RateLimitRule {
    const cached = this.#ruleMemo.get(ns);
    if (cached) return cached;
    let best = this.#global;
    for (const rule of this.#rules) {
      if (rule.ns.length === 0) continue;
      if (ns === rule.ns || ns.startsWith(`${rule.ns}.`)) {
        if (rule.ns.length > best.ns.length) best = rule;
      }
    }
    if (this.#ruleMemo.size >= RULE_MEMO_CAP) this.#ruleMemo.clear();
    this.#ruleMemo.set(ns, best);
    return best;
  }

  #take(bucket: Bucket, now: number): boolean {
    const elapsed = Math.max(0, now - bucket.last) / 1000;
    bucket.last = now;
    bucket.tokens = Math.min(
      bucket.rule.burst,
      bucket.tokens + elapsed * bucket.rule.perSec,
    );
    if (bucket.tokens < 1) return false;
    bucket.tokens -= 1;
    return true;
  }
}
